import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Icon from '../components/Icon';

function SkeletonCard() {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ height: 12, background: 'var(--color-surface-2)', borderRadius: 4, width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 28, background: 'var(--color-surface-2)', borderRadius: 4, width: '40%', animation: 'pulse 1.5s ease-in-out infinite' }} />
    </div>
  );
}

function ClientPickerSheet({ clients, onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const filtered = clients.filter(c =>
    (c.first_name + ' ' + c.last_name).toLowerCase().includes(query.toLowerCase())
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', background: 'var(--color-surface)', borderRadius: '16px 16px 0 0', padding: 20, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Select Client</div>
        <input className="form-input" placeholder="Search clients..." value={query} onChange={e => setQuery(e.target.value)}
          style={{ marginBottom: 12 }} autoFocus />
        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.length === 0 && <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', marginTop: 16 }}>No clients found</p>}
          {filtered.map(c => (
            <button key={c.id} onClick={() => onSelect(c)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--color-bg)', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(215,38,56,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="icon-user.png" size={18} />
              </div>
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>{c.first_name} {c.last_name}</span>
            </button>
          ))}
        </div>
        <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [nutritionCount, setNutritionCount] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard').catch(() => null),
      api.get('/clients').catch(() => []),
      api.get('/nutrition').catch(() => []),
      api.get('/dashboard/quote').catch(() => null),
    ]).then(([s, cli, plans, q]) => {
      setStats(s);
      setClients(Array.isArray(cli) ? cli : []);
      setNutritionCount(Array.isArray(plans) ? plans.length : 0);
      if (q) setQuote(q);
    }).finally(() => setLoading(false));
  }, []);

  function handleSelectClient(client) {
    setShowClientPicker(false);
    navigate('/clients/' + client.id + '?tab=Metrics');
  }

  const statCards = stats ? [
    { label: 'Active Clients',   icon: 'icon-user.png',     value: stats.active_clients,               link: '/clients'  },
    { label: "Today's Sessions", icon: 'icon-calendar.png', value: stats.today_sessions.length,        link: '/schedule' },
    { label: 'Monthly Revenue',  icon: 'icon-invoice.png',  value: '$' + stats.monthly_revenue.toFixed(2), link: '/invoices' },
    { label: 'Upcoming',         icon: 'icon-clock.png',    value: stats.upcoming.length,              link: '/schedule' },
  ] : [];

  return (
    <div>
      <div className="page-header">
        <h1>
          <img src="/icons/icon-menu-logo.png" alt="" style={{ height: 32, objectFit: 'contain' }} onError={(e) => e.target.style.display='none'} />
          {trainer?.business_name || 'Dashboard'}
        </h1>
      </div>

      <div className="page-content">
        {/* Stat grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          {loading
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            : statCards.map(({ label, icon, value, link }) => (
              <div key={label} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer' }} onClick={() => navigate(link)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon name={icon} size={18} />
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</span>
                </div>
                <span style={{ fontSize: 28, fontWeight: 700 }}>{value}</span>
              </div>
            ))
          }
        </div>

        {/* Today's appointments */}
        {!loading && stats?.today_sessions?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stats.today_sessions.map(a => (
                <div key={a.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer' }} onClick={() => navigate('/schedule')}>
                  <img src="/icons/icon-user.png" alt="" style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-surface-2)', flexShrink: 0 }} onError={(e) => e.target.style.display='none'} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.first_name ? a.first_name + ' ' + a.last_name : 'Session'}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {new Date(a.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {new Date(a.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {a.location && ' · ' + a.location}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming appointments */}
        {!loading && stats?.upcoming?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Upcoming</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stats.upcoming.map(a => (
                <div key={a.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer' }} onClick={() => navigate('/schedule')}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(215,38,56,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="icon-clock.png" size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.first_name ? a.first_name + ' ' + a.last_name : 'Appointment'}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {new Date(a.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {new Date(a.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent sessions */}
        {!loading && stats?.recent_progress?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <Icon name="icon-chart.png" size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Recent Sessions
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stats.recent_progress.map(s => (
                <div key={s.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', cursor: 'pointer' }} onClick={() => navigate('/clients/' + (s.client_id || s.id))}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.first_name} {s.last_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{new Date(s.date).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && quote && (
          <div className="card" style={{ padding: '20px 22px', marginBottom: 20, borderLeft: '3px solid var(--color-primary)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ fontSize: 48, lineHeight: 1, color: 'var(--color-primary)', opacity: 0.18, fontFamily: 'Georgia, serif', position: 'absolute', top: 4, left: 14, userSelect: 'none' }}>&ldquo;</div>
            <div style={{ paddingLeft: 8 }}>
              <p style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 500, color: 'var(--color-text)', lineHeight: 1.55, fontStyle: 'italic' }}>{quote.body}</p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--color-primary)', fontWeight: 600, letterSpacing: '0.03em' }}>— {quote.attribution}</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {!loading && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Actions</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button onClick={() => navigate('/programs')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '20px 12px', cursor: 'pointer', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(215,38,56,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="icon-program.png" size={22} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Add Program</span>
              </button>
              <button onClick={() => navigate('/metrics')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '20px 12px', cursor: 'pointer', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(215,38,56,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="icon-chart.png" size={22} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Metrics</span>
              </button>
              <button onClick={() => navigate('/nutrition')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '20px 12px', cursor: 'pointer', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 'var(--radius)', textAlign: 'center', gridColumn: 'span 2' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(215,38,56,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="icon-nutrition.png" size={22} />
                </div>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', display: 'block' }}>Nutrition Plans</span>
                  {nutritionCount !== null && (
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{nutritionCount} plan{nutritionCount !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </button>
            </div>
          </div>
        )}

      </div>

      {showClientPicker && <ClientPickerSheet clients={clients} onSelect={handleSelectClient} onClose={() => setShowClientPicker(false)} />}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}
